import { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router';
import { useQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type { ColDef, GridApi } from 'ag-grid-community';
import { graphql } from '../../../gql';
import type { AssetHistoryQuery } from '../../../gql/graphql';
import { ASSET_HISTORY_TYPES_QUERY } from '../../../api/master-data-queries';
import { DataGrid } from '../../../components/data-grid';
import { LoadingOverlay, SelectInput, displayDate, displayDecimal, displayInteger, displayText } from '../../../components/ui';
import type { AssetDetailFragment } from '../AssetDetailPage';

const ASSET_HISTORY_QUERY = graphql(`
  query AssetHistory($assetId: Int!) {
    assetHistory(assetId: $assetId) {
      canViewCosts
      rows {
        id date eventTypeId eventTypeLocalizationKey eventTypeDescription
        currentSmr comment costOfParts costOfLabour currencyCode
      }
    }
  }
`);

type HistoryRow = NonNullable<AssetHistoryQuery['assetHistory']>['rows'][number];

function columns(canViewCosts: boolean): ColDef<HistoryRow>[] {
  const cost = (field: 'costOfParts' | 'costOfLabour', headerName: string): ColDef<HistoryRow> => ({
    field,
    headerName,
    flex: 1,
    cellStyle: { textAlign: 'right' },
    valueFormatter: (params) => params.value == null ? '-' :
      `${displayDecimal(Number(params.value))}${params.data?.currencyCode ? ` ${params.data.currencyCode}` : ''}`,
  });

  return [
    { field: 'date', headerName: 'Date', flex: 1, sort: 'desc', valueFormatter: (params) => displayDate(params.value as string | null) },
    { field: 'eventTypeDescription', headerName: 'Event Type', flex: 2, filter: false, valueFormatter: (params) => displayText(params.value) },
    { field: 'currentSmr', headerName: 'Current SMR', flex: 1, cellStyle: { textAlign: 'right' }, valueFormatter: (params) => displayInteger(params.value) },
    { field: 'comment', headerName: 'Comment', flex: 3, wrapText: true, autoHeight: true, valueFormatter: (params) => displayText(params.value) },
    ...(canViewCosts ? [cost('costOfParts', 'Cost of Parts'), cost('costOfLabour', 'Cost of Labour')] : []),
  ];
}

/** Read-only current Asset Notes for an Asset confirmed accessible by the parent route. */
export function AssetHistoryTab() {
  const asset = useOutletContext<AssetDetailFragment | null>();
  const [selectedTypes, setSelectedTypes] = useState<number[]>([]);
  const gridApi = useRef<GridApi<HistoryRow> | null>(null);
  const { data, loading, error, refetch } = useQuery(ASSET_HISTORY_QUERY, {
    variables: { assetId: asset?.id ?? 0 },
    skip: asset == null,
  });
  const { data: typesData } = useQuery(ASSET_HISTORY_TYPES_QUERY, { skip: asset == null });
  useEffect(() => { gridApi.current?.onFilterChanged(); }, [selectedTypes]);
  const history = data?.assetHistory;
  const options = (typesData?.assetHistoryTypes?.nodes ?? [])
    .filter((type) => type.id !== 9)
    .slice()
    .sort((first, second) => first.order - second.order)
    .map((type) => ({ value: type.id, label: type.assetHistoryTypeDescription }));

  return (
    <section className="py-6" aria-label="Service tracker">
      <LoadingOverlay visible={loading} />
      {error ? (
        <div role="alert">
          <Typography color="error">{error.message}</Typography>
          <Button onClick={() => void refetch()}>Retry</Button>
        </div>
      ) : history && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="w-60 max-w-full">
              <SelectInput
                label="Event Type"
                multiple
                value={selectedTypes}
                options={options}
                onChange={(event) => setSelectedTypes(event.target.value as number[])}
                renderValue={(values) => (values as number[]).map((id) => options.find((option) => option.value === id)?.label).join(', ')}
              />
            </div>
            {selectedTypes.length > 0 && <Button onClick={() => setSelectedTypes([])}>Clear</Button>}
          </div>
          {history.rows.length === 0 ? (
            <Typography color="text.secondary">No results found.</Typography>
          ) : (
            <DataGrid
              rowData={history.rows}
              columnDefs={columns(history.canViewCosts)}
              isExternalFilterPresent={() => selectedTypes.length > 0}
              doesExternalFilterPass={(node) => selectedTypes.includes(node.data?.eventTypeId ?? -1)}
              onGridReady={(event) => { gridApi.current = event.api; }}
              pagination
              paginationPageSize={20}
              paginationPageSizeSelector={[10, 20, 30, 40]}
              domLayout="autoHeight"
              height="auto"
            />
          )}
        </>
      )}
    </section>
  );
}